import { Box, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import React from 'react';
import { Column } from './MoDataTable';
import { MoLink } from './MoLink';
import { SessionAttendanceInput, SessionAttendanceStatus } from './SessionAttendanceStatus';
import { SupervisorName } from './SupervisorName';
import { SuperviseeAttendee, SuperviseeAttendeeList } from './SuperviseeAttendeeList';
import {
  formatDateTimeWithWeekdayOrdinalAndYear,
  getDurationTextFromMinutes,
} from '../utils/dateUtils';

export type SessionsRouteConfig = {
  sessionDetailPath?: (sessionId: number) => string | undefined;
  supervisorProfilePath?: (supervisorId: number) => string | undefined;
  superviseeProfilePath?: (superviseeUserId: number) => string | undefined;
};

export function buildDateColumn<T extends { id: number; scheduledAt: string }>(
  routeConfig?: Pick<SessionsRouteConfig, 'sessionDetailPath'>,
): Column<T> {
  return {
    header: 'Date',
    content(session) {
      const scheduledAt = DateTime.fromISO(session.scheduledAt);
      const dateNode = (
        <>
          <Box>{formatDateTimeWithWeekdayOrdinalAndYear(scheduledAt)}</Box>
          <Box>{scheduledAt.toLocaleString(DateTime.TIME_SIMPLE)}</Box>
        </>
      );
      const to = routeConfig?.sessionDetailPath?.(session.id);
      return to ? (
        <MoLink to={to} fontWeight="bold">
          {dateNode}
        </MoLink>
      ) : (
        <Box fontWeight="bold">{dateNode}</Box>
      );
    },
  };
}

export function buildDurationColumn<T extends { duration: number }>(): Column<T> {
  return {
    header: 'Duration',
    content: (session) => getDurationTextFromMinutes(session.duration),
  };
}

export function buildTypeColumn<T extends { sessionType: { displayName: string } }>(): Column<T> {
  return {
    header: 'Type',
    content: (session) => session.sessionType.displayName,
  };
}

export function buildSupervisorColumn<
  T extends { supervisor: { id: number; fullName: string } },
>({
  routeConfig,
  showBadge = false,
  getIsInternal,
  canLink,
}: {
  routeConfig?: Pick<SessionsRouteConfig, 'supervisorProfilePath'>;
  showBadge?: boolean;
  getIsInternal?: (session: T) => boolean;
  canLink?: (session: T) => boolean;
} = {}): Column<T> {
  return {
    header: 'Supervisor',
    content(session) {
      const linkable = canLink?.(session) ?? true;
      const to = linkable ? routeConfig?.supervisorProfilePath?.(session.supervisor.id) : undefined;
      const isInternal = getIsInternal?.(session) ?? false;
      return (
        <SupervisorName
          name={session.supervisor.fullName}
          to={to}
          showBadge={showBadge && !isInternal}
        />
      );
    },
  };
}

export function buildSuperviseesColumn<T extends { sessionAttendees: SuperviseeAttendee[] }>({
  showAttendance,
  header = 'Supervisees',
  routeConfig,
}: {
  showAttendance: boolean;
  header?: string;
  routeConfig?: Pick<SessionsRouteConfig, 'superviseeProfilePath'>;
}): Column<T> {
  return {
    header,
    content(session) {
      if (session.sessionAttendees.length === 0) {
        return <Typography color="text.secondary">—</Typography>;
      }
      return (
        <SuperviseeAttendeeList
          attendees={session.sessionAttendees}
          showAttendance={showAttendance}
          getProfilePath={routeConfig?.superviseeProfilePath}
        />
      );
    },
  };
}

export function buildAttendanceColumn<T>({
  getAttendee,
}: {
  getAttendee: (session: T) => SessionAttendanceInput | null | undefined;
}): Column<T> {
  return {
    header: 'Attendance',
    content: (session) => <SessionAttendanceStatus attendee={getAttendee(session)} />,
  };
}
